'use client';

/**
 * ConfirmDialog — branded confirmation primitive built on shadcn AlertDialog.
 *
 * Replaces 19 in-tree `window.confirm()` calls that
 *  1. Render the OS-native dialog (un-brandable, looks broken on prod)
 *  2. Block the JS event loop for the duration the dialog is open — verified
 *     live on prod, the entire renderer freezes for 30+ seconds
 *
 * Two usage patterns:
 *
 *  Declarative (state-owned by caller):
 *    <ConfirmDialog
 *      open={open}
 *      onOpenChange={setOpen}
 *      title="Archive team?"
 *      description="Members stay in the system; the team won't accept new positions."
 *      confirmLabel="Archive"
 *      variant="destructive"
 *      onConfirm={async () => { await archive(); }}
 *    />
 *
 *  Imperative (via useConfirm hook — see hooks/use-confirm.tsx):
 *    const confirm = useConfirm();
 *    if (!(await confirm({ title: 'Archive team?', ... }))) return;
 *
 * Async-safe: if onConfirm returns a Promise, the dialog waits for it to
 * resolve before closing. If onConfirm throws, the dialog stays open so the
 * user can see an error toast and retry.
 */

import * as React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface ConfirmDialogProps {
  /** Whether the dialog is open. Controlled. */
  open: boolean;
  /** Called when the dialog should close (cancel, ESC, click-outside, or after a successful confirm). */
  onOpenChange: (open: boolean) => void;
  /** Required heading copy. Question form preferred ("Archive team?"). */
  title: string;
  /** Optional sub-text explaining what will happen. */
  description?: string;
  /** Primary action label. Default: "Confirm". Match the verb to the action ("Archive", "Delete", "Remove"). */
  confirmLabel?: string;
  /** Secondary action label. Default: "Cancel". */
  cancelLabel?: string;
  /** Visual treatment of the primary button. Use `destructive` for irreversible loss. */
  variant?: 'default' | 'destructive';
  /** Action handler. May return a Promise — dialog waits for resolution before closing. */
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  onConfirm,
}: ConfirmDialogProps) {
  const [pending, setPending] = React.useState(false);

  const handleConfirm = async (e: React.MouseEvent) => {
    // Stop AlertDialog's default close-on-action so we control the close
    // timing — needed so async onConfirm can still fail-keep-open.
    e.preventDefault();
    setPending(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // Keep dialog open on failure. Caller is expected to surface a toast
      // (typically via useActionWithFeedback) explaining the error.
    } finally {
      setPending(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {/* Always render a description for a11y; visually hide when caller
              didn't supply one. Radix requires SOMETHING aria-describedby'd
              so screen readers have context beyond the title. */}
          {description ? (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          ) : (
            <AlertDialogDescription className="sr-only">
              Confirm or cancel this action.
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={pending}
            className={cn(
              variant === 'destructive' &&
                buttonVariants({ variant: 'destructive' }),
            )}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
